import { useCallback, useEffect, useRef, useState } from 'react'
import type { Edge } from '@xyflow/react'
import type { DiagramStore } from '../store/diagramStore'
import type { DiagramType } from '../types/schema'
import { parseDiagramData } from '../utils/parser'
import { serializeToDiagramData } from '../utils/serializer'
import { createYWebsocketSyncProvider, createCollabUndoManager } from './SyncProvider'
import {
  roomToDiagramData,
  writeDiagramDataToRoom,
  getRoomNodesMap,
  getRoomEdgesMap,
  getRoomContainersMap,
  getRoomMetaMap,
} from './roomBridge'
import {
  presenceFromAwareness,
  makeLocalPresence,
  type PresencePeer,
  type PresencePoint,
} from './presence'

/**
 * useCollab — opt-in real-time collaboration for the diagram editor.
 *
 * When `enabled`, this hook joins a y-websocket room (per the SHARED ROOM
 * CONTRACT) and keeps the diagram store and the room's Y.Doc in two-way sync:
 *   room -> store: observe the nodes/edges/meta maps; on a foreign-origin
 *     transaction, parse the room's DiagramData through parseDiagramData and push
 *     it into the store silently (no undo pollution).
 *   store -> room: subscribe to the store; debounce ~80ms, then serialize and
 *     upsert/delete into the room under the local origin.
 *
 * ECHO GUARD: a module-scoped `applyingRemote` flag prevents the store->room
 * writer from running while we are applying a remote change, and the room->store
 * observer ignores transactions authored by our own local origin.
 *
 * When NOT enabled, this hook does nothing (creates no provider, imports no
 * transport) and the editor behaves exactly as before.
 */

const DEBOUNCE_MS = 80

export interface UseCollabOptions {
  /** WebSocket server URL, e.g. "ws://localhost:1234". */
  url: string
  /** Room name === roomId (the MCP session_id or explicit room_id). */
  room: string
  /** Diagram type used when serializing the store into the room. */
  diagramType?: DiagramType
  /** Opt-in switch. When false/undefined the hook is inert. */
  enabled?: boolean
  /**
   * Cache the room in IndexedDB for instant reload / offline (RTD-2). Defaults
   * to true; set false to disable local persistence.
   */
  offline?: boolean
}

/** What the editor publishes about the local user (cursor + selection). */
export interface LocalPresenceInput {
  /** Live cursor in flow coordinates, or null/undefined when off-canvas. */
  cursor?: PresencePoint | null
  /** Ids of the elements the local user currently has selected. */
  selection?: string[]
}

export interface UseCollabResult {
  connected: boolean
  provider: ReturnType<typeof createYWebsocketSyncProvider> | null
  /** Remote peers (excludes self). Empty when collab is off or no peers. */
  peers: PresencePeer[]
  /**
   * Publish the local user's live presence (cursor/selection) to the awareness
   * channel. A no-op when collab is off or the transport hasn't connected yet.
   */
  publishPresence: (input: LocalPresenceInput) => void
}

export function useCollab(store: DiagramStore, opts: UseCollabOptions): UseCollabResult {
  const { url, room, diagramType = 'architecture', enabled = false, offline = true } = opts

  const [connected, setConnected] = useState(false)
  const [peers, setPeers] = useState<PresencePeer[]>([])
  const providerRef = useRef<ReturnType<typeof createYWebsocketSyncProvider> | null>(null)
  // Latest local presence (cursor/selection), kept in a ref so publishPresence
  // works without re-running the effect. The effect re-publishes from this ref.
  const localPresenceRef = useRef<LocalPresenceInput>({})
  // Swapped to the live awareness writer once the transport connects; a no-op
  // otherwise so callers can always invoke publishPresence safely.
  const publishRef = useRef<(input: LocalPresenceInput) => void>(() => {})
  const publishPresence = useCallback((input: LocalPresenceInput) => {
    publishRef.current(input)
  }, [])

  useEffect(() => {
    if (!enabled) {
      setConnected(false)
      setPeers([])
      providerRef.current = null
      publishRef.current = () => {}
      return
    }

    // Echo guard: true while we apply a remote change to the store, so the
    // store->room writer early-returns and we don't bounce the change back.
    let applyingRemote = false
    let debounceTimer: ReturnType<typeof setTimeout> | null = null
    let disposed = false

    const provider = createYWebsocketSyncProvider({ url, room, offline })
    providerRef.current = provider
    const doc = provider.doc
    const localOrigin = 'user:' + doc.clientID

    const nodesMap = getRoomNodesMap(doc)
    const edgesMap = getRoomEdgesMap(doc)
    const containersMap = getRoomContainersMap(doc)
    const metaMap = getRoomMetaMap(doc)

    // --- room -> store ---------------------------------------------------
    const applyRoomToStore = () => {
      applyingRemote = true
      try {
        const data = roomToDiagramData(doc)
        const result = parseDiagramData({ ...data, diagramType: data.diagramType ?? diagramType })
        // Force edge type to "custom" unless already "smart" (contract).
        const edges: Edge[] = result.edges.map((e) =>
          e.type === 'smart' ? e : { ...e, type: 'custom' },
        )
        store.getState().setNodesSilent(result.nodes)
        store.getState().setEdgesSilent(edges)
      } finally {
        applyingRemote = false
      }
    }

    const observer = (_events: unknown, txn: { origin: unknown }) => {
      // Ignore our own writes — we already have that state locally.
      if (txn.origin === localOrigin) return
      applyRoomToStore()
    }
    nodesMap.observe(observer as never)
    edgesMap.observe(observer as never)
    containersMap.observe(observer as never)
    metaMap.observe(observer as never)

    // --- store -> room ---------------------------------------------------
    const flushStoreToRoom = () => {
      if (disposed) return
      const { nodes, edges } = store.getState()
      const data = serializeToDiagramData(nodes, edges, diagramType)
      writeDiagramDataToRoom(doc, data, localOrigin)
    }

    const unsubscribeStore = store.subscribe(() => {
      if (applyingRemote) return
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(flushStoreToRoom, DEBOUNCE_MS)
    })

    // --- undo manager scoped to local-origin edits ----------------------
    const undoManager = createCollabUndoManager(doc, { trackedOrigins: [localOrigin] })

    // --- connect + seed --------------------------------------------------
    provider.connect()
    setConnected(true)

    // If the room is empty on join, seed it from the current store so the first
    // peer publishes its diagram. (When the room already has content, the
    // observer above will pull it down.)
    if (nodesMap.size === 0 && edgesMap.size === 0) {
      flushStoreToRoom()
    } else {
      applyRoomToStore()
    }

    // --- presence (awareness) -------------------------------------------
    // The awareness channel only exists once the lazy y-websocket import has
    // resolved inside provider.connect(). Poll briefly for it, then attach a
    // change listener (remote peers -> renderable list) and wire the local
    // publisher. All presence work is gated by this — when collab is off the
    // effect early-returns above and none of this runs.
    let awarenessAttached: import('y-protocols/awareness').Awareness | null = null
    let onAwarenessChange: (() => void) | null = null

    const publishLocal = (aware: import('y-protocols/awareness').Awareness) => {
      const live = localPresenceRef.current
      aware.setLocalState(
        makeLocalPresence(doc.clientID, {
          cursor: live.cursor ?? undefined,
          selection: live.selection,
        }),
      )
    }

    const attachAwareness = () => {
      const aware = provider.awareness
      if (!aware || disposed) return
      awarenessAttached = aware
      // Publish initial identity + any presence captured before connect.
      publishLocal(aware)
      publishRef.current = (input: LocalPresenceInput) => {
        localPresenceRef.current = input
        publishLocal(aware)
      }
      onAwarenessChange = () => {
        if (disposed) return
        setPeers(presenceFromAwareness(aware, doc.clientID))
      }
      aware.on('change', onAwarenessChange)
      onAwarenessChange()
    }

    // Try immediately, then poll until the transport's awareness is available.
    attachAwareness()
    let awarenessPoll: ReturnType<typeof setInterval> | null = null
    if (!awarenessAttached) {
      awarenessPoll = setInterval(() => {
        if (disposed || awarenessAttached) {
          if (awarenessPoll) clearInterval(awarenessPoll)
          return
        }
        attachAwareness()
        if (awarenessAttached && awarenessPoll) clearInterval(awarenessPoll)
      }, 200)
    }

    return () => {
      disposed = true
      if (debounceTimer) clearTimeout(debounceTimer)
      if (awarenessPoll) clearInterval(awarenessPoll)
      if (awarenessAttached && onAwarenessChange) {
        awarenessAttached.off('change', onAwarenessChange)
      }
      publishRef.current = () => {}
      nodesMap.unobserve(observer as never)
      edgesMap.unobserve(observer as never)
      containersMap.unobserve(observer as never)
      metaMap.unobserve(observer as never)
      unsubscribeStore()
      undoManager.destroy()
      provider.disconnect()
      providerRef.current = null
      setConnected(false)
      setPeers([])
    }
  }, [store, url, room, diagramType, enabled, offline])

  return { connected, provider: providerRef.current, peers, publishPresence }
}
